import { useMemo } from 'react';

import type { History } from 'history';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { FlagOptions } from '@atlassian/forge-ui/iframe';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { useHistory__UNSAFE, useLocation } from 'src/router/hooks';

import { useFlag } from './flag';

type ForgeBridgeProps = {
  includeHistory?: boolean;
  extensionBasePathEndPosition?: number;
};

type ForgeBridgeHook = {
  showFlag: (options: FlagOptions) => boolean;
  closeFlag: (options: { id: string }) => boolean;
  history?: History;
  extensionBasePath?: string;
};

/*
 * A hook which generates bridge props which can be directly
 * passed into a Forge extension point from Frontbucket.
 *
 * The `extensionBasePathEndIndex` prop is used to extract the base path of the extension from the
 * current URL.
 */
export const useForgeBridge = ({
  includeHistory,
  extensionBasePathEndPosition,
}: ForgeBridgeProps = {}): ForgeBridgeHook => {
  const { dismissFlag, showFlag } = useFlag({ enableForPdvs: true });
  const history = useHistory__UNSAFE();
  const location = useLocation();
  const enableForgeHistory = useStatsigGate(
    StatsigFeatureKeys.enableForgeHistory
  );

  const extensionBasePath = useMemo(() => {
    if (extensionBasePathEndPosition) {
      return location.pathname
        .split('/')
        .slice(0, extensionBasePathEndPosition)
        .join('/');
    }
    return undefined;
  }, [location.pathname, extensionBasePathEndPosition]);

  const mapForgeToBbFlag = ({
    id,
    title,
    description,
    type,
    appearance,
    isAutoDismiss,
    onClose,
    actions,
  }: FlagOptions) => ({
    id: `${id}`,
    title: title || '',
    description,
    iconType: appearance || type,
    autoDismiss: isAutoDismiss,
    onDismissed: onClose,
    nonIntlActions: actions?.map(({ text, onClick }) => ({
      content: text,
      onClick,
    })),
  });

  const baseProps = useMemo(
    () => ({
      showFlag: (options: FlagOptions) => {
        showFlag(mapForgeToBbFlag(options));
        return true;
      },
      closeFlag: ({ id }: { id: string }) => {
        dismissFlag(id);
        return true;
      },
    }),
    [showFlag, dismissFlag]
  );

  const props = useMemo(() => {
    if (enableForgeHistory && includeHistory && extensionBasePath) {
      return {
        ...baseProps,
        history,
        extensionBasePath,
      };
    }
    return baseProps;
  }, [
    enableForgeHistory,
    includeHistory,
    baseProps,
    history,
    extensionBasePath,
  ]);

  return props;
};
